/**
 * The swap's picture of a container's prompt groups (21/10/2026): the rows
 * the container editor shows (a group with a random rule, or the rules
 * picked in it — the editor's own picksFromIds), the row the failing rule
 * sits in, and what a swap sends — that whole row out, the new pick in.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenPromptSwapView } from '@trafficpulse/shared';
import type { TreeGroupLike } from '@/features/campaign-texts/utils/group-tree.utils';

import { idsFromPicks, picksFromIds, type PromptPick } from '../../../editor/creative/picks';

export const picksOf = (view: DropGenPromptSwapView, groups: TreeGroupLike[]): PromptPick[] => picksFromIds(view.groupIds, groups);

export const isFailing = (pick: PromptPick, view: DropGenPromptSwapView): boolean =>
  view.failingId !== null && (pick.folderId === view.failingId || pick.ruleIds.includes(view.failingId));

export const nameOf = (groups: TreeGroupLike[], id: string): string => groups.find((g) => g.id === id)?.name ?? 'a deleted group';

/** "Transactional · TRANSACTIONAL 2, TRANSACTIONAL 3" / "Soft texts · a random rule". */
export const pickLabel = (pick: PromptPick, groups: TreeGroupLike[]): string =>
  `${nameOf(groups, pick.folderId)} · ${pick.ruleIds.length > 0 ? pick.ruleIds.map((id) => nameOf(groups, id)).join(', ') : 'a random rule'}`;

/** The request: the failing row's ids out, the new pick's in — null until a different pick is made. */
export function swapBody(view: DropGenPromptSwapView, groups: TreeGroupLike[], next: PromptPick | null): { replaceIds: string[]; toGroupIds: string[] } | null {
  const failing = picksOf(view, groups).find((p) => isFailing(p, view));
  if (!failing || !next?.folderId) return null;
  const replaceIds = idsFromPicks([failing]);
  const toGroupIds = idsFromPicks([next]);
  return toGroupIds.every((id) => replaceIds.includes(id)) ? null : { replaceIds, toGroupIds };
}
